<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="dialogVisible"
    width="800px"
    :close-on-click-modal="false"
    :before-close="handleDialogClose"
  >
    <!-- 6步步骤条 -->
    <el-steps
      :active="activeStep"
      finish-status="success"
      style="margin-bottom: 25px"
    >
      <el-step title="选择合同模板"></el-step>
      <el-step title="选择负责人与房屋"></el-step>
      <el-step title="填写乙方信息"></el-step>
      <el-step title="房屋与合同信息"></el-step>
      <el-step title="租赁与付款信息"></el-step>
      <el-step title="政策信息"></el-step>
    </el-steps>

    <!-- 步骤内容区域 -->
    <el-form
      ref="contractForm"
      :model="formData"
      :rules="formRules"
      label-width="160px"
      class="contract-form"
    >
      <!-- 步骤0：选择合同模板 -->
      <template v-if="activeStep === 0">
        <el-form-item
          label="合同模板"
          prop="templateType"
          class="required-item"
        >
          <el-select
            v-model="formData.templateType"
            placeholder="请选择合同模板"
            @change="handleTemplateChange"
            style="width: 100%"
            key="template-select"
          >
            <el-option
              label="房屋租赁协议模板（装修抵租金.不含采暖费版本）"
              value="houseRent"
            ></el-option>
            <el-option
              label="光谷房屋租赁协议最新版本"
              value="houseRentNew"
            ></el-option>
            <el-option
              label="房屋租赁协议模板（终版.含采暖费）"
              value="houseRentHeating"
            ></el-option>
            <el-option
              label="房屋租赁协议模板（终版.不含采暖费版本）"
              value="houseRentNotHeating"
            ></el-option>
            <el-option
              label="房屋租赁协议模板（全手工录入版）"
              value="houseRentManual"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-card v-if="formData.templateType" class="template-desc">
          <div slot="header">模板说明</div>
          <p>{{ getTemplateDesc() }}</p>
          <div
            class="template-tips"
            v-if="formData.templateType === 'houseRent'"
          >
            <i class="el-icon-info"></i>
            提示：该模板需额外填写装修金额、投入金额等字段
          </div>
          <div
            class="template-tips"
            v-if="formData.templateType === 'houseRentNew'"
          >
            <i class="el-icon-info"></i>
            提示：该模板适用于工业用地/厂房租赁，需填写甲方邮寄地址
          </div>
          <div
            class="template-tips"
            v-if="formData.templateType === 'houseRentManual'"
          >
            <i class="el-icon-warning"></i>
            提示：全手工录入模式，系统不自动计算租金、采暖费及付款明细，所有金额需手动填写
          </div>
        </el-card>
      </template>

      <!-- 步骤1：选择负责人与房屋 -->
      <template v-if="activeStep === 1">
        <!-- 合同负责人 -->
        <el-form-item
          label="合同负责人"
          prop="contractManager"
          class="required-item"
        >
          <el-select
            v-model="formData.contractManager"
            :placeholder="
              loadingStaff
                ? '加载中...'
                : staffList.length === 0
                ? '未查询到负责人'
                : '请选择合同负责人'
            "
            style="width: 100%"
            :disabled="loadingStaff || staffList.length === 0"
          >
            <el-option
              v-for="staff in staffList"
              :key="staff.userId"
              :label="staff.name"
              :value="staff.userId"
            ></el-option>
          </el-select>
        </el-form-item>

        <!-- 选择房屋 - 远程搜索模式 -->
        <el-form-item
          label="选择房屋"
          prop="selectedHouse"
          class="required-item"
        >
          <el-select
            ref="houseSelect"
            v-model="formData.selectedHouse"
            multiple
            collapse-tags
            filterable
            remote
            reserve-keyword
            :remote-method="searchRoomList"
            :loading="loadingRooms"
            :placeholder="
              loadingRooms
                ? '加载中...'
                : filteredRoomList.length === 0
                ? '未查询到房屋数据'
                : '请输入房号/名称搜索并选择'
            "
            style="width: 100%"
            :disabled="loadingRooms"
            @change="handleHouseChange"
            @focus="onHouseSelectFocus"
            :key="houseSelectKey"
          >
            <el-option
              v-for="room in filteredRoomList"
              :key="`room-${room.roomId}`"
              :label="`${room.roomName || '未知房屋'}（${
                room.builtUpArea || 0
              }㎡）`"
              :value="room.roomId"
            ></el-option>
            <div
              v-if="!loadingRooms && filteredRoomList.length === 0"
              class="empty-option"
              style="padding: 10px 0; text-align: center; color: #999"
            >
              {{ searchQuery ? "未找到匹配的房屋" : "未查询到房屋数据" }}
            </div>
          </el-select>
          <div
            style="
              font-size: 12px;
              color: #666;
              margin-top: 6px;
              display: flex;
              gap: 15px;
            "
          >
            <span>房屋列表数量：{{ roomList.length }} 条</span>
            <span>已选房屋数量：{{ formData.selectedHouse.length }} 个</span>
          </div>
        </el-form-item>

        <!-- 已选房屋详情 -->
        <el-card
          v-if="formData.selectedHouse.length > 0"
          class="selected-house-card"
        >
          <div slot="header">已选房屋详情</div>
          <el-table
            ref="houseTable"
            :data="getSelectedHouseDetails()"
            border
            style="width: 100%"
            :key="houseAmountUpdated"
          >
            <el-table-column
              prop="roomName"
              label="房屋名称"
              align="center"
            ></el-table-column>
            <el-table-column
              prop="builtUpArea"
              label="建筑面积(㎡)"
              align="center"
            ></el-table-column>
            <el-table-column
              prop="roomSubTypeName"
              label="房屋类型"
              align="center"
            ></el-table-column>
            <el-table-column
              prop="stateName"
              label="房屋状态"
              align="center"
            ></el-table-column>
            <el-table-column
                prop="rentAmount"
                label="单套租金(元)"
                align="center"
                :formatter="formatRentAmount"
            ></el-table-column>
            <!-- 采暖费列：仅含采暖费模板显示，手工模式不显示 -->
            <el-table-column
              v-if="['houseRentHeating'].includes(formData.templateType)"
              prop="heatingAmount"
              label="单套采暖费(元)"
              align="center"
              :formatter="formatHeatingAmount"
            ></el-table-column>
          </el-table>
          <div
            class="selected-house-summary"
            style="margin-top: 10px; text-align: right"
          >
            <span style="margin-right: 20px"
              >总面积：{{ formData.houseArea || 0 }} ㎡</span
            >
            <template
              v-if="['houseRentHeating'].includes(formData.templateType)"
            >
              <span style="margin-right: 20px">
                基础租金合计：{{ Number(totalRoomAmount || 0).toFixed(2) }} 元
              </span>
              <span style="margin-right: 20px"
                >采暖费合计：{{ formData.heatingFee || 0 }} 元</span
              >
            </template>
          </div>
        </el-card>
      </template>

      <!-- 步骤2：填写乙方信息 -->
      <template v-if="activeStep === 2">
        <el-form-item label="乙方公司全称" prop="partyBName">
          <el-input
            v-model="formData.partyBName"
            placeholder="请输入乙方公司全称"
            maxlength="100"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方注册地址" prop="partyBAddress">
          <el-input
            v-model="formData.partyBAddress"
            placeholder="请输入乙方公司注册地址"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方联系人" prop="partyBContact">
          <el-input
            v-model="formData.partyBContact"
            placeholder="请输入乙方联系人姓名"
            maxlength="20"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方联系电话" prop="partyBPhone">
          <el-input
            v-model="formData.partyBPhone"
            placeholder="请输入乙方联系电话"
            maxlength="20"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方邮箱" prop="partyBEmail">
          <el-input
            v-model="formData.partyBEmail"
            placeholder="请输入乙方邮箱"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方邮寄地址" prop="partyBmAilingAddress">
          <el-input
            v-model="formData.partyBmAilingAddress"
            placeholder="请输入乙方邮寄地址"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="乙方法定代表人" prop="partyBLegal">
          <el-input
            v-model="formData.partyBLegal"
            placeholder="请输入乙方法定代表人姓名"
            maxlength="20"
            show-word-limit
          ></el-input>
        </el-form-item>
      </template>

      <!-- 步骤3：房屋与合同信息 -->
      <template v-if="activeStep === 3">
        <!-- 采暖费输入 - 仅含采暖费模板显示，手工模式不显示 -->
        <el-form-item
          v-if="['houseRentHeating'].includes(formData.templateType)"
          label="采暖费（元）"
          prop="heatingFee"
          class="required-item"
        >
          <el-input
            v-model.number="formData.heatingFee"
            placeholder="请输入采暖费金额"
            type="number"
            min="0"
            step="0.01"
            @change="calcAllRentAmount"
          ></el-input>
        </el-form-item>

        <el-form-item label="房屋详细地址" prop="houseAddress">
          <el-input
            v-model="formData.houseAddress"
            placeholder="例：X号楼第XXX层XXX号房屋"
            show-word-limit
            tooltip="已根据选中房屋自动拼接，可手动修改"
          ></el-input>
        </el-form-item>
        <el-form-item label="合同签订日期" prop="signDate">
          <el-date-picker
            v-model="formData.signDate"
            type="date"
            placeholder="选择合同签订日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="房屋建筑面积(㎡)" prop="houseArea">
          <el-input
            v-model.number="formData.houseArea"
            placeholder="请输入房屋建筑面积"
            type="number"
            min="0"
            step="0.01"
            :readonly="formData.selectedHouse.length > 0 && !isManualMode"
            tooltip="已根据选中房屋自动计算，可手动修改"
            @change="calcAllRentAmount"
          ></el-input>
        </el-form-item>
        <el-form-item label="房屋用途" prop="buildingUsage">
          <el-select
            v-model="formData.buildingUsage"
            placeholder="请选择房屋用途"
            style="width: 100%"
          >
            <template v-if="formData.templateType === 'houseRentNew'">
              <el-option label="工业用地" value="工业用地"></el-option>
              <el-option label="厂房" value="厂房"></el-option>
            </template>
            <template v-else>
              <el-option label="办公" value="办公"></el-option>
              <el-option label="商业" value="商业"></el-option>
            </template>
          </el-select>
        </el-form-item>
        <template v-if="formData.templateType === 'houseRent'">
          <el-form-item label="装修金额(万元)" prop="decorationAmount">
            <el-input
              v-model.number="formData.decorationAmount"
              placeholder="请输入装修金额"
              type="number"
              min="0"
              step="0.01"
            ></el-input>
          </el-form-item>
          <el-form-item label="投入金额(万元)" prop="investmentAmount">
            <el-input
              v-model.number="formData.investmentAmount"
              placeholder="请输入投入金额"
              type="number"
              min="0"
              step="0.01"
            ></el-input>
          </el-form-item>
          <el-form-item label="保证金金额(万元)" prop="depositAmount">
            <el-input
              v-model.number="formData.depositAmount"
              placeholder="请输入保证金金额"
              type="number"
              min="0"
            ></el-input>
          </el-form-item>
        </template>
      </template>

      <!-- 步骤4：租赁与付款信息 -->
      <template v-if="activeStep === 4">
        <el-form-item label="保证金" prop="marginDeposit">
          <el-input
            v-model="formData.marginDeposit"
            placeholder="请输入保证金金额"
            @input="limitTwoDecimal"
            @blur="formatDecimal"
          ></el-input>
        </el-form-item>
        <el-form-item label="孵化周期(年)" prop="incubationPeriod">
          <el-input
            v-model.number="formData.incubationPeriod"
            placeholder="请输入孵化周期，支持小数"
            type="number"
            min="1"
            step="0.01"
            @change="handleIncubationPeriodChange"
          ></el-input>
        </el-form-item>
        <el-form-item label="孵化周期时间范围" prop="incubationPeriodTimeRange">
          <el-date-picker
            v-model="formData.incubationPeriodTimeRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
            @change="updateIncubationPeriodText"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="房屋装修期" prop="houseDecorationPeriodRange">
          <el-date-picker
            v-model="formData.houseDecorationPeriodRange"
            type="daterange"
            range-separator="至"
            start-placeholder="装修开始日期"
            end-placeholder="装修结束日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
            @change="updateHouseDecorationPeriodText"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="租赁期限" prop="leaseTermRange">
          <el-date-picker
            v-model="formData.leaseTermRange"
            type="daterange"
            range-separator="至"
            start-placeholder="租赁开始日期"
            end-placeholder="租赁结束日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
            @change="updateLeaseTermText"
          ></el-date-picker>
        </el-form-item>

        <el-divider content-position="left">租金标准日期</el-divider>
        <el-form-item label="租金标准开始日期" prop="rentStart">
          <el-date-picker
            v-model="formData.rentStart"
            type="date"
            placeholder="选择租金标准开始日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
            @change="handleRentStartDateChange"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="租金标准截止日期" prop="rentEnd">
          <el-date-picker
            v-model="formData.rentEnd"
            type="date"
            placeholder="选择租金标准截止日期"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            style="width: 100%"
            @change="handleRentEndDateChange"
          ></el-date-picker>
        </el-form-item>

        <!-- 租赁支付方式选择 -->
        <el-form-item
          label="租赁支付方式"
          prop="paymentMethod"
          class="required-item"
        >
          <el-select
            v-model="formData.paymentMethod"
            placeholder="请选择租赁支付方式"
            style="width: 100%"
            @change="handlePaymentMethodChange"
          >
            <el-option label="月付" value="monthly"></el-option>
            <el-option label="季付" value="quarterly"></el-option>
            <el-option label="半年付" value="half-yearly"></el-option>
            <el-option label="年付" value="yearly"></el-option>
          </el-select>
        </el-form-item>

        <!-- 动态付款信息 -->
        <el-divider content-position="left">付款信息</el-divider>
        <div
          v-for="(payment, index) in formData.payments"
          :key="index"
          class="payment-group"
        >
          <div class="payment-group-header">
            <span class="payment-group-title">第{{ index + 1 }}笔付款信息</span>
            <el-button
              type="text"
              icon="el-icon-delete"
              @click="removePayment(index)"
              :disabled="formData.payments.length <= 1"
              class="delete-btn"
            >
              删除
            </el-button>
          </div>
          <el-form-item
            :label="`付款日期 ${index + 1}`"
            :prop="`payments[${index}].paymentDate`"
            :rules="formRules.paymentDate"
          >
            <el-date-picker
              v-model="payment.paymentDate"
              type="date"
              placeholder="选择付款日期"
              format="yyyy年MM月dd日"
              value-format="yyyy-MM-dd"
              style="width: 100%"
            ></el-date-picker>
          </el-form-item>
          <el-form-item
              :label="`租赁起始日期 ${index + 1}`"
              :prop="`payments[${index}].startDate`"
              :rules="formRules.startDate"
          >
            <el-date-picker
                v-model="payment.startDate"
                type="date"
                placeholder="选择租赁起始日期"
                format="yyyy年MM月dd日"
                value-format="yyyy-MM-dd"
                style="width: 100%"
                @change="calcSinglePaymentDays(index)"
            ></el-date-picker>
          </el-form-item>
          <el-form-item
              :label="`租赁截止日期 ${index + 1}`"
              :prop="`payments[${index}].endDate`"
              :rules="formRules.endDate"
          >
            <el-date-picker
                v-model="payment.endDate"
                type="date"
                placeholder="选择租赁截止日期"
                format="yyyy年MM月dd日"
                value-format="yyyy-MM-dd"
                style="width: 100%"
                @change="calcSinglePaymentDays(index)"
            ></el-date-picker>
          </el-form-item>
          <el-form-item
              :label="`计租天数 ${index + 1}`"
              :prop="`payments[${index}].rentalDays`"
              :rules="formRules.rentalDays"
          >
            <el-input
                v-model.number="payment.rentalDays"
                placeholder="选择日期后自动计算，可手动修改"
                type="number"
                min="1"
            ></el-input>
          </el-form-item>
          <el-form-item
              :label="`租金金额 ${index + 1}`"
              :prop="`payments[${index}].rentAmount`"
              :rules="formRules.rentAmount"
          >
            <el-input
                v-model="payment.rentAmount"
                :placeholder="isManualMode ? '请手动输入租金金额' : '由总金额除以组数自动计算'"
                type="number"
                min="0"
                step="0.01"
                :readonly="!isManualMode"
                :tooltip="isManualMode ? '手工模式可自由编辑' : '由总金额除以组数自动计算，不可手动修改'"
                @change="reCalcTotalFromPayments"
            ></el-input>
          </el-form-item>
          <el-divider v-if="index < formData.payments.length - 1"></el-divider>
        </div>
        <el-button
          type="primary"
          icon="el-icon-plus"
          @click="addPayment"
          style="margin-top: 10px"
        >
          添加付款信息
        </el-button>

        <el-divider content-position="left">汇总信息</el-divider>
        <!-- 仅非手工模式显示自动计算明细 -->
        <div
          v-if="
            ['houseRentHeating'].includes(formData.templateType) &&
            !isManualMode
          "
          style="
            margin-bottom: 10px;
            padding: 10px;
            background: #f5f5f5;
            border-radius: 4px;
          "
        >
          <div
            style="
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            "
          >
            <span>房屋基础租金：</span>
            <span>{{ totalRoomAmount.toFixed(2) }} 元</span>
          </div>
          <div
            style="
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
            "
          >
            <span>采暖费金额：</span>
            <span>{{ (formData.heatingFee || 0).toFixed(2) }} 元</span>
          </div>
          <div style="display: flex; justify-content: space-between">
            <span>租金标准计算总天数：</span>
            <span>{{ calcRentStandardDays() }} 天</span>
          </div>
        </div>

        <el-form-item label="租金总计" prop="total">
          <el-input
              v-model.number="formData.total"
              :placeholder="isManualMode ? '由付款明细自动汇总计算' : '系统自动计算，可手动修改'"
              type="number"
              min="0"
              step="0.01"
              :readonly="isManualMode"
              @change="handleTotalChange"
          ></el-input>
        </el-form-item>

        <el-form-item label="租金（大写）" prop="rent">
          <el-input
            v-model="formData.rent"
            placeholder="系统自动计算，可手动修改"
            maxlength="50"
            show-word-limit
            readonly
            tooltip="由总面积、租金标准、租金标准总天数、采暖费自动核算"
            @change="handleRentChange"
          ></el-input>
        </el-form-item>
        <el-form-item
          v-if="!isManualMode"
          label="租金标准(元/天)含税"
          prop="rentRate"
          class="required-item"
        >
          <el-input
            v-model.number="formData.rentRateCount"
            type="number"
            min="0"
            step="0.01"
            @input="handleRentRateInput"
            @change="calcAllRentAmount"
          ></el-input>
        </el-form-item>
      </template>

      <!-- 步骤5：政策信息 -->
      <template v-if="activeStep === 5">
        <el-form-item label="政策来源" prop="policySource">
          <el-input
            v-model="formData.policySource"
            placeholder="请输入政策来源（非必填）"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="政策信息" prop="policyInfo">
          <el-input
            v-model="formData.policyInfo"
            placeholder="请输入政策信息（非必填）"
            type="textarea"
            :rows="5"
            maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>
      </template>
    </el-form>

    <!-- 弹窗底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <template v-if="activeStep === 0">
        <el-button
          type="primary"
          @click="goToNextStep"
          :disabled="!formData.templateType"
        >
          下一步
        </el-button>
      </template>
      <template v-if="activeStep >= 1 && activeStep <= 4">
        <el-button @click="goToPrevStep">上一步</el-button>
        <el-button
          type="primary"
          @click="goToNextStep"
          :loading="isNavigating"
          :disabled="isNavigating"
        >
          下一步
        </el-button>
      </template>
      <template v-if="activeStep === 5">
        <el-button @click="goToPrevStep">上一步</el-button>
        <el-button
          type="primary"
          @click="submitContractForm"
          :loading="isSaving"
        >
          <template v-if="!isSaving">提交生成Word</template>
          <template v-else>提交中...</template>
        </el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import {
  Dialog,
  Form,
  FormItem,
  Input,
  Select,
  Option,
  DatePicker,
  Button,
  Steps,
  Step,
  Card,
  Divider,
  Table,
  TableColumn,
} from "element-ui";
import {
  queryWordByContract,
  getStaffInfos,
  queryRooms,
  queryRoomAmounts,
  queryContractDetail,
} from "@/api/contract/newContractManageApi";
import { getCommunityId } from "@/api/community/communityApi";

export default {
  name: "ContractModal",
  components: {
    ElDialog: Dialog,
    ElForm: Form,
    ElFormItem: FormItem,
    ElInput: Input,
    ElSelect: Select,
    ElOption: Option,
    ElDatePicker: DatePicker,
    ElButton: Button,
    ElSteps: Steps,
    ElStep: Step,
    ElCard: Card,
    ElDivider: Divider,
    ElTable: Table,
    ElTableColumn: TableColumn,
  },
  data() {
    return {
      isEditMode: false,
      currentContractId: null,
      houseSelectKey: 1,
      paymentDebounceTimer: null,
      isNavigating: false,
      dialogVisible: false,
      dialogTitle: "新增合同信息",
      isSaving: false,
      activeStep: 0,
      loadingStaff: false,
      loadingRooms: false,
      loadingRoomAmounts: false,
      loadingDetail: false,
      staffList: [],
      roomList: [],
      filteredRoomList: [],
      searchQuery: "",
      roomMap: {},
      rentRatePrecise: 0,
      heatingFeePrecise  : 0,
      roomAmountMap: {},
      totalRoomAmount: 0,
      communityId: "",
      isFirstEnterStep1: true,
      houseAmountUpdated: false,
      forceRefreshRoom: false,
      formData: {
        templateType: "",
        heatingFee: 0,
        contractManager: "",
        selectedHouse: [],
        partyBName: "",
        partyBAddress: "",
        partyBContact: "",
        partyBPhone: "",
        partyBEmail: "",
        partyBmAilingAddress: "",
        partyBLegal: "",
        rent: "",
        houseAddress: "",
        signDate: "",
        houseArea: 0,
        buildingUsage: "",
        decorationAmount: "",
        investmentAmount: "",
        depositAmount: "",
        marginDeposit: "",
        incubationPeriod: "",
        incubationPeriodTimeRange: [],
        incubationPeriodTime: "",
        houseDecorationPeriodRange: [],
        houseDecorationPeriod: "",
        leaseTermRange: [],
        leaseTerm: "",
        rentStart: "",
        rentEnd: "",
        paymentMethod: "",
        payments: [
          {
            paymentDate: "",
            paymentId: "",
            startDate: "",
            endDate: "",
            rentalDays: "",
            rentAmount: "",
            rentOnlyAmount: 0,
            heatingOnlyAmount: 0,
          },
        ],
        total: 0,
        rentRate: "",
        rentRateCount: 0,
        policySource: "",
        policyInfo: "",
      },
      formRules: {
        templateType: [
          { required: true, message: "请先选择合同模板", trigger: "change" },
        ],
        heatingFee: [
          {
            required: true,
            message: "请输入采暖费金额",
            trigger: "blur",
            validator: (rule, value, callback) => {
              if (!["houseRentHeating"].includes(this.formData.templateType)) {
                callback();
                return;
              }
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("采暖费必须为非负数"));
              } else if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
                callback(new Error("采暖费最多保留两位小数"));
              } else {
                callback();
              }
            },
          },
        ],
        contractManager: [
          { required: true, message: "请选择合同负责人", trigger: "change" },
        ],
        selectedHouse: [
          { required: true, message: "请至少选择一个房屋", trigger: "change" },
          {
            type: "array",
            min: 1,
            message: "至少选择一个房屋",
            trigger: "change",
          },
        ],
        partyBName: [
          { required: true, message: "请输入乙方公司全称", trigger: "blur" },
          { max: 100, message: "公司全称不能超过100个字符", trigger: "blur" },
        ],
        partyBAddress: [
          { required: true, message: "请输入乙方注册地址", trigger: "blur" },
          { max: 200, message: "注册地址不能超过200个字符", trigger: "blur" },
        ],
        partyBContact: [
          { required: true, message: "请输入乙方联系人姓名", trigger: "blur" },
          {
            max: 20,
            message: "乙方联系人姓名不能超过20个字符",
            trigger: "blur",
          },
        ],
        partyBPhone: [
          { required: true, message: "请输入乙方联系电话", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const phoneRegex = /^1[3-9]\d{9}$/;
              const telRegex = /^0\d{2,3}-\d{7,8}$/;
              if (
                !value ||
                (!phoneRegex.test(value) && !telRegex.test(value))
              ) {
                callback(new Error("请输入正确的手机号或固定电话"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        partyBLegal: [
          {
            required: true,
            message: "请输入乙方法定代表人姓名",
            trigger: "blur",
          },
          {
            max: 20,
            message: "法定代表人姓名不能超过20个字符",
            trigger: "blur",
          },
        ],
        rent: [
          {
            required: true,
            message: "请输入租金（中文大写）",
            trigger: "blur",
          },
          {
            pattern:
              /^(零|壹|贰|叁|肆|伍|陆|柒|捌|玖|拾|佰|仟|万|亿|元|角|分|整)+$/,
            message: "请输入合法的中文大写金额（如：壹万元整）",
            trigger: "blur",
          },
        ],
        houseAddress: [
          { required: true, message: "请输入房屋详细地址", trigger: "blur" },
        ],
        signDate: [
          { required: true, message: "请选择合同签订日期", trigger: "change" },
        ],
        houseArea: [
          { required: true, message: "请输入房屋建筑面积", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("建筑面积必须为非负数"));
              } else if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
                callback(new Error("建筑面积最多保留两位小数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        buildingUsage: [
          { required: true, message: "请选择房屋用途", trigger: "change" },
        ],
        decorationAmount: [
          {
            required: true,
            message: "请输入装修金额（万元）",
            trigger: "blur",
          },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("装修金额必须为非负数"));
              } else if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
                callback(new Error("装修金额最多保留两位小数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        investmentAmount: [
          {
            required: true,
            message: "请输入投入金额（万元）",
            trigger: "blur",
          },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("投入金额必须为非负数"));
              } else if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
                callback(new Error("投入金额最多保留两位小数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        depositAmount: [
          {
            required: true,
            message: "请输入保证金金额（万元）",
            trigger: "blur",
          },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("保证金金额必须为非负数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        marginDeposit: [
          { required: true, message: "请输入保证金金额", trigger: "blur" },
          {
            pattern: /^\d+(\.\d{1,2})?$/,
            message: "金额最多保留两位小数",
            trigger: "blur",
          },
        ],
        incubationPeriod: [
          { required: true, message: "请输入孵化周期（年）", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              // 校验：必须是数字 且 大于等于1
              if (isNaN(num) || num < 1) {
                callback(
                  new Error("孵化周期必须为大于等于1的正数，可填写小数"),
                );
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        incubationPeriodTimeRange: [
          {
            required: true,
            message: "请选择孵化周期时间范围",
            trigger: "change",
          },
        ],
        leaseTermRange: [
          { required: true, message: "请选择租赁期限", trigger: "change" },
        ],
        rentStart: [
          {
            required: true,
            message: "请选择租金标准开始日期",
            trigger: "change",
          },
        ],
        rentEnd: [
          {
            required: true,
            message: "请选择租金标准截止日期",
            trigger: "change",
          },
        ],
        paymentMethod: [
          { required: true, message: "请选择租赁支付方式", trigger: "change" },
        ],
        paymentDate: [
          { required: true, message: "请选择付款日期", trigger: "change" },
        ],
        startDate: [
          { required: true, message: "请选择租赁起始日期", trigger: "change" },
        ],
        endDate: [
          { required: true, message: "请选择租赁截止日期", trigger: "change" },
        ],
        rentalDays: [
          { required: true, message: "请输入计租天数", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (!Number.isInteger(num) || num < 1) {
                callback(new Error("计租天数必须为正整数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        rentAmount: [
          {
            validator: (rule, value, callback) => {
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("租金金额不能为负数"));
              } else {
                callback();
              }
            },
            trigger: "change",
          },
        ],
        total: [
          { required: true, message: "租金总计不能为空", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              // 手工模式下不校验总计等于明细之和，允许手工调整
              if (this.isManualMode) {
                callback();
                return;
              }
              const total = this.formData.payments.reduce((acc, item) => {
                return acc + (Number(item.rentAmount) || 0);
              }, 0);
              const sum = Number(total.toFixed(2));
              if (Number(value) !== sum) {
                callback(new Error(`总计金额应等于所有付款金额之和（${sum}）`));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        rentRate: [
          { required: true, message: "请输入租金标准", trigger: "blur" },
          {
            validator: (rule, value, callback) => {
              // 手工模式跳过租金标准校验
              if (this.isManualMode) {
                callback();
                return;
              }
              const num = Number(value);
              if (isNaN(num) || num < 0) {
                callback(new Error("租金标准不能为负数"));
              } else if (!/^\d+(\.\d{1,2})?$/.test(value.toString())) {
                callback(new Error("租金标准最多保留两位小数"));
              } else {
                callback();
              }
            },
            trigger: "blur",
          },
        ],
        policySource: [],
        policyInfo: [],
      },
    };
  },
  computed: {
    // 手工模式判断
    isManualMode() {
      return this.formData.templateType === "houseRentManual";
    },
  },
  watch: {
    activeStep(newVal) {
      if (newVal === 1) {
        this.$nextTick(async () => {
          if (this.isFirstEnterStep1) {
            if (!this.currentContractId) {
              this.formData.selectedHouse = [];
            }
            await this.loadStaffList();
            await this.loadRoomList();
            this.isFirstEnterStep1 = false;
          } else {
            if (this.staffList.length === 0) {
              await this.loadStaffList();
            }
            if (this.roomList.length === 0) {
              await this.loadRoomList();
            }
          }
          this.$forceUpdate();
        });
      }
    },
    "formData.templateType"() {
      this.formData.selectedHouse = [];
    },
    "formData.total": {
      handler() {
        this.formData.rent = this.numberToChinese(Number(this.formData.total));
        // 手工模式下修改总计不分摊到明细
        if (!this.isManualMode) {
          this.calcPaymentAmounts();
        }
      },
    },
    "formData.selectedHouse": {
      deep: true,
      async handler(newVal) {
        if (newVal.length === 0) {
          this.formData.houseAddress = "";
          this.formData.houseArea = 0;
          this.formData.rent = "";
          this.formData.total = 0;
          this.totalRoomAmount = 0;
          this.roomAmountMap = {};
          this.houseAmountUpdated = !this.houseAmountUpdated;
          return;
        }
        const houseNames = newVal.map((roomId) => {
          const room = this.roomMap[roomId];
          return room ? room.roomName : "未知房屋";
        });
        this.formData.houseAddress = houseNames.join("、");
        const totalArea = newVal.reduce((sum, roomId) => {
          const room = this.roomMap[roomId];
          return sum + (room ? Number(room.builtUpArea) || 0 : 0);
        }, 0);
        this.formData.houseArea = Number(totalArea.toFixed(2));
        // 手工模式下选择房屋不自动计算金额
        if (!this.isManualMode) {
          await this.calcTotalRoomAmount(newVal);
        }
      },
    },
    "formData.leaseTermRange": {
      deep: true,
      handler() {
        this.updateLeaseTermText(this.formData.leaseTermRange);
      },
    },
    "formData.rentRate": {
      handler() {
        // 手工模式跳过自动计算
        if (this.isManualMode) return;
        this.calcAllRentAmount();
      },
    },
    "formData.heatingFee": {
      handler() {
        if (this.isManualMode) return;
        this.calcAllRentAmount();
      },
    },
    "formData.houseArea": {
      handler() {
        if (this.isManualMode) return;
        this.calcAllRentAmount();
      },
    },
    "formData.rentStart": {
      handler() {
        if (this.isManualMode) return;
        if (this.formData.paymentMethod && this.formData.rentEnd) {
          this.generatePayments();
        }
      },
    },
    "formData.rentEnd": {
      handler() {
        if (this.isManualMode) return;
        if (this.formData.paymentMethod && this.formData.rentStart) {
          this.generatePayments();
        }
      },
    },
  },
  methods: {
    open(contractId) {
      this.currentContractId = contractId || null;
      this.isEditMode = !!contractId;
      this.dialogVisible = true;
      this.isSaving = false;
      this.activeStep = 0;
      this.isFirstEnterStep1 = true;
      if (this.currentContractId) {
        this.forceRefreshRoom = true;
      }
      this.resetFormData();

      this.$nextTick(async () => {
        if (this.$refs.contractForm) {
          this.$refs.contractForm.resetFields();
          this.$refs.contractForm.clearValidate();
        }
        this.calcPaymentAmounts();

        if (this.currentContractId) {
          this.dialogTitle = "修改合同信息";
          await this.loadContractDetail();
        } else {
          this.dialogTitle = "新增合同信息";
        }
      });
    },

    async loadContractDetail() {
      this.loadingDetail = true;
      try {
        const res = await queryContractDetail({
          contractId: this.currentContractId,
        });
        if (res.code !== 0) throw new Error(res.msg || "获取合同详情失败");
        const detail = res.data;

        this.formData.templateType = detail.templateCode;
        this.formData.heatingFee = Number(detail.heatingFee || 0);
        this.formData.contractManager = detail.contractManager;

        this.formData.partyBName = detail.partyBName;
        this.formData.partyBAddress = detail.partyBAddress;
        this.formData.partyBContact = detail.partyBContact;
        this.formData.partyBPhone = detail.partyBPhone;
        this.formData.partyBEmail = detail.partyBEmail;
        this.formData.partyBmAilingAddress = detail.partyBmAilingAddress;
        this.formData.partyBLegal = detail.partyBLegal;

        this.formData.rent = detail.rent;
        this.formData.houseAddress = detail.houseAddress;
        this.formData.signDate = detail.signDate
          ? this.cnDateToNormal(detail.signDate)
          : "";
        this.formData.houseArea = Number(detail.houseArea || 0);
        this.formData.buildingUsage = detail.buildingUsage;

        this.formData.decorationAmount = detail.decorationAmount;
        this.formData.investmentAmount = detail.investmentAmount;
        this.formData.depositAmount = detail.depositAmount;
        this.formData.marginDeposit = detail.marginDeposit;

        this.formData.incubationPeriod = detail.incubationPeriod;
        this.formData.incubationPeriodTimeRange =
          detail.incubationPeriodTime || [];
        this.formData.houseDecorationPeriodRange =
          detail.houseDecorationPeriod || [];
        this.formData.leaseTermRange = detail.leaseTerm || [];

        this.formData.rentStart = detail.startTime
          ? this.cnDateToNormal(detail.startTime)
          : "";
        this.formData.rentEnd = detail.endTime
          ? this.cnDateToNormal(detail.endTime)
          : "";

        this.formData.total = Number(detail.total || 0);
        this.formData.rentRate = Number(detail.rentRate || 0);
        this.formData.rentRateCount = Number(detail.rentRateCount || 0);

        this.formData.policySource = detail.policySource;
        this.formData.policyInfo = detail.policyInfo;

        if (
          Array.isArray(detail.contractPaymentDtoList) &&
          detail.contractPaymentDtoList.length > 0
        ) {
          this.formData.payments = detail.contractPaymentDtoList.map((p) => {
            return {
              paymentId: p.paymentId,
              paymentDate: p.paymentDate
                ? this.cnDateToNormal(p.paymentDate)
                : "",
              startDate: p.startDate ? this.cnDateToNormal(p.startDate) : "",
              endDate: p.endDate ? this.cnDateToNormal(p.endDate) : "",
              rentalDays: p.rentalDays,
              rentAmount: Number(p.rentAmount || 0),
              rentOnlyAmount: Number(p.rentOnlyAmount || 0),
              heatingOnlyAmount: Number(p.heatingOnlyAmount || 0),
            };
          });
        } else {
          this.formData.payments = [
            {
              paymentDate: "",
              startDate: "",
              endDate: "",
              rentalDays: "",
              rentAmount: "",
              rentOnlyAmount: 0,
              heatingOnlyAmount: 0,
            },
          ];
        }
        this.formData.paymentMethod = detail.paymentMethod;
        await this.$nextTick();
        this.calcPaymentAmounts();

        await this.loadStaffList();
        await this.loadRoomList();

        let raw = [];
        try {
          raw =
            typeof detail.selectedHouse === "string"
              ? JSON.parse(detail.selectedHouse)
              : detail.selectedHouse || [];
        } catch {
          raw = [];
        }
        const selectedIds = (Array.isArray(raw) ? raw : []).map((id) =>
          String(id),
        );

        selectedIds.forEach((roomId) => {
          const roomInfo = this.roomMap[roomId];
          if (roomInfo) {
            const exist = this.filteredRoomList.some(
              (item) => String(item.roomId) === roomId,
            );
            if (!exist) {
              this.filteredRoomList.push(roomInfo);
            }
          }
        });

        this.formData.selectedHouse = selectedIds;

        this.houseSelectKey++;
        await this.$nextTick();

        const tempArr = [...this.formData.selectedHouse];
        this.formData.selectedHouse = [];
        await this.$nextTick();
        this.formData.selectedHouse = tempArr;

        await this.$nextTick();
        // 手工模式编辑回填不触发自动重算
        if (!this.isManualMode) {
          this.calcAllRentAmount();
        }
        this.reCalcTotalFromPayments();
      } catch (err) {
        console.error("加载合同详情异常：", err);
        this.$message.error(err.message || "加载合同详情失败");
        this.dialogVisible = false;
      } finally {
        this.loadingDetail = false;
      }
    },

    cnDateToChinese(dateStr) {
      if (!dateStr) return "";
      return dateStr.replace(/(\d{4})-(\d{2})-(\d{2})/, "$1年$2月$3日");
    },
    cnDateToNormal(dateStr) {
      if (!dateStr) return "";
      return dateStr.replace(/(\d{4})年(\d{2})月(\d{2})日/, "$1-$2-$3");
    },

    resetFormData() {
      this.formData = {
        templateType: "",
        heatingFee: 0,
        contractManager: "",
        selectedHouse: [],
        partyBName: "",
        partyBAddress: "",
        partyBContact: "",
        partyBPhone: "",
        partyBEmail: "",
        partyBmAilingAddress: "",
        partyBLegal: "",
        rent: "",
        houseAddress: "",
        signDate: "",
        houseArea: 0,
        buildingUsage: "",
        decorationAmount: "",
        investmentAmount: "",
        depositAmount: "",
        marginDeposit: "",
        incubationPeriod: "",
        incubationPeriodTimeRange: [],
        incubationPeriodTime: "",
        houseDecorationPeriodRange: [],
        houseDecorationPeriod: "",
        leaseTermRange: [],
        leaseTerm: "",
        rentStart: "",
        rentEnd: "",
        paymentMethod: "",
        payments: [
          {
            paymentDate: "",
            startDate: "",
            endDate: "",
            rentalDays: "",
            rentAmount: "",
            rentOnlyAmount: 0,
            heatingOnlyAmount: 0,
          },
        ],
        total: 0,
        rentRate: "",
        rentRateCount: 0,
        policySource: "",
        policyInfo: "",
      };

      this.roomList = [];
      this.filteredRoomList = [];
      this.roomMap = {};
      this.roomAmountMap = {};
      this.totalRoomAmount = 0;
      this.searchQuery = "";
      this.forceRefreshRoom = true;
    },

    limitTwoDecimal(val) {
      let str = val.replace(/[^\d.]/g, "");
      let dotCount = (str.match(/\./g) || []).length;
      if (dotCount > 1) {
        str = str.slice(0, str.lastIndexOf("."));
      }
      let dotIndex = str.indexOf(".");
      if (dotIndex > -1) {
        let intPart = str.substring(0, dotIndex);
        let decPart = str.substring(dotIndex + 1, dotIndex + 3);
        str = intPart + "." + decPart;
      }
      this.formData.marginDeposit = str;
    },

    formatDecimal() {
      let val = this.formData.marginDeposit;
      if (!val || val === ".") {
        this.formData.marginDeposit = "";
        return;
      }
      let num = parseFloat(val);
      if (!isNaN(num)) {
        this.formData.marginDeposit = num.toFixed(2);
      }
    },

    getTemplateDesc() {
      const descMap = {
        houseRent:
          "本模板适用于装修抵扣租金且不含采暖费的房屋租赁场景，需填写装修金额、投入金额等特殊字段，租金支付方式分多期（可添加）。",
        houseRentNew:
          "光谷地区最新房屋租赁协议模板，适用于工业用地和厂房租赁场景，包含邮寄地址字段，租金分多期（可添加）支付。",
        houseRentHeating:
          "包含采暖费条款的房屋租赁协议终版，适用于办公或商业用途的房屋租赁，租金分多期（可添加）支付。",
        houseRentNotHeating:
          "不含采暖费条款的房屋租赁协议终版，适用于办公或商业用途的房屋租赁，租金分多期（可添加）支付。",
        houseRentManual:
          "全手工录入模式，系统不进行任何自动计算，租金标准、采暖费、付款明细及总计金额均需手动填写，适用于特殊定制化合同场景。",
      };
      return (
        descMap[this.formData.templateType] || "该模板适用于房屋租赁场景..."
      );
    },

    async handleTemplateChange() {
      this.formData.buildingUsage = "";
      if (!["houseRentHeating"].includes(this.formData.templateType)) {
        this.formData.heatingFee = 0;
      }
      if (this.formData.templateType !== "houseRent") {
        this.formData.decorationAmount = "";
        this.formData.investmentAmount = "";
        this.formData.depositAmount = "";
      }
      // 手工模式重置租金标准
      if (this.isManualMode) {
        this.formData.rentRate = "";
        this.formData.rentRateCount = 0;
      }
      this.formData.selectedHouse = [];
      this.roomList = [];
      this.filteredRoomList = [];
      this.roomMap = {};
      this.roomAmountMap = {};
      this.totalRoomAmount = 0;
      this.searchQuery = "";
      this.forceRefreshRoom = true;
      if (this.$refs.houseSelect) {
        this.$refs.houseSelect.clearValue();
      }
      await this.loadRoomList();
    },

    async loadStaffList() {
      if (this.staffList.length > 0) return;
      this.loadingStaff = true;
      try {
        const res = await getStaffInfos({ page: 1, row: 1000 });
        this.staffList = res.staffs || [];
        if (this.staffList.length === 0) {
          this.$message.warning("未查询到负责人数据");
        }
      } catch (error) {
        console.error("加载负责人失败：", error);
        this.$message.error("加载负责人失败，请重试");
        this.staffList = [];
      } finally {
        this.loadingStaff = false;
      }
    },

    async loadRoomList() {
      if (this.roomList.length > 0 && !this.forceRefreshRoom) {
        this.filteredRoomList = this.roomList;
        return Promise.resolve(this.roomList);
      }
      if (this.loadingRooms) return Promise.resolve();

      this.loadingRooms = true;
      this.roomList = [];
      this.filteredRoomList = [];
      try {
        if (!this.communityId) {
          this.$message.warning("社区ID未获取到，无法加载房屋数据");
          return Promise.reject(new Error("communityId为空"));
        }

        const res = await queryRooms({
          roomType: "1010301",
          roomSubType: "",
          flag: "",
          page: 1,
          row: 1000,
          communityId: this.communityId,
        });

        let newRoomList = res.rooms || [];
        this.roomList = newRoomList;
        this.filteredRoomList = newRoomList;

        const newRoomMap = newRoomList.reduce((map, room) => {
          const rid = String(room.roomId);
          map[rid] = {
            roomId: rid,
            roomName: room.apartmentName || room.roomName,
            builtUpArea: Number(room.roomArea || room.builtUpArea) || 0,
            ...room,
          };
          return map;
        }, {});
        this.roomMap = newRoomMap;
        this.forceRefreshRoom = false;
        this.$forceUpdate();
        return Promise.resolve(newRoomList);
      } catch (error) {
        console.error("加载房屋失败：", error);
        this.$message.error("加载房屋失败，请重试");
        return Promise.reject(error);
      } finally {
        this.loadingRooms = false;
      }
    },

    onHouseSelectFocus() {
      if (!this.searchQuery) {
        this.filteredRoomList = [...this.roomList];
      }
    },

    async searchRoomList(query) {
      this.searchQuery = query;
      this.loadingRooms = true;
      try {
        if (!query) {
          this.filteredRoomList = this.roomList;
          return;
        }
        const lowerQuery = query.toLowerCase();
        this.filteredRoomList = this.roomList.filter((room) => {
          const roomName = (room.roomName || "").toLowerCase();
          const roomId = (room.roomId || "").toLowerCase();
          return roomName.includes(lowerQuery) || roomId.includes(lowerQuery);
        });
      } catch (error) {
        console.error("搜索房屋失败:", error);
        this.$message.error("搜索房屋失败");
        this.filteredRoomList = [];
      } finally {
        this.loadingRooms = false;
      }
    },

    chineseToNumber(chinese) {
      if (!chinese || typeof chinese !== "string") return 0;
      const digitMap = {
        零: 0,
        壹: 1,
        贰: 2,
        叁: 3,
        肆: 4,
        伍: 5,
        陆: 6,
        柒: 7,
        捌: 8,
        玖: 9,
      };
      const unitMap = { 拾: 10, 佰: 100, 仟: 1000, 万: 10000, 亿: 100000000 };
      let result = 0;
      let temp = 0;
      let unit = 1;

      chinese = chinese.replace(/整$/, "");
      const parts = chinese.split(/[元角分]/);
      const yuanPart = parts[0] || "";
      const jiaoPart = parts[1] || "";
      const fenPart = parts[2] || "";

      for (let i = yuanPart.length - 1; i >= 0; i--) {
        const char = yuanPart[i];
        if (digitMap[char] !== undefined) {
          temp += digitMap[char] * unit;
        } else if (unitMap[char] !== undefined) {
          if (unit > unitMap[char]) temp *= unitMap[char];
          unit = unitMap[char];
          if (unit >= 10000) {
            result += temp;
            temp = 0;
          }
        }
      }
      result += temp;
      if (jiaoPart) result += digitMap[jiaoPart] * 0.1;
      if (fenPart) result += digitMap[fenPart] * 0.01;
      return Number(result.toFixed(2));
    },

    handlePaymentMethodChange() {
      this.isEditMode = false;
      if (this.paymentDebounceTimer) clearTimeout(this.paymentDebounceTimer);
      this.paymentDebounceTimer = setTimeout(() => {
        this.generatePayments();
        this.paymentDebounceTimer = null;
      }, 100);
    },

    numberToChinese(num) {
      if (num === 0) return "零元整";
      const digits = [
        "零",
        "壹",
        "贰",
        "叁",
        "肆",
        "伍",
        "陆",
        "柒",
        "捌",
        "玖",
      ];
      const units = ["", "拾", "佰", "仟", "万", "拾", "佰", "仟", "亿"];
      let str = "";
      const yuan = Math.floor(num);
      const jiaoFen = Math.round((num - yuan) * 100);
      const jiao = Math.floor(jiaoFen / 10);
      const fen = jiaoFen % 10;

      const yuanStr = yuan.toString();
      const len = yuanStr.length;
      for (let i = 0; i < len; i++) {
        const d = parseInt(yuanStr[i]);
        const pos = len - 1 - i;
        const u = units[pos];
        if (d === 0) {
          if (i === 0 || yuanStr[i - 1] !== "0") str += digits[d];
        } else {
          str += digits[d] + u;
        }
      }
      str += "元";
      if (jiao > 0) str += digits[jiao] + "角";
      if (fen > 0) str += digits[fen] + "分";
      if (jiao === 0 && fen === 0) str += "整";
      if (str.startsWith("零元")) str = str.replace("零元", "");
      return str;
    },

    handleRentRateInput() {
      const val = Number(this.formData.rentRateCount);
      if (isNaN(val) || val <= 0) {
        this.formData.total = 0;
        this.formData.rent = "零元整";
        this.calcPaymentAmounts();
        return;
      }
      // 手动输入时，高精度值同步为输入值（两位精度，无误差）
      this.rentRatePrecise = Number(this.formData.rentRate) || 0;
      this.heatingFeePrecise   = Number(this.formData.heatingFee) || 0;
      this.calcAllRentAmount();
    },

    handleTotalChange() {
      // 总计变化：手工模式只更新大写，不分摊明细
      this.formData.rent = this.numberToChinese(Number(this.formData.total));
      if (!this.isManualMode) {
        this.calcPaymentAmounts();
      }
    },

    handleHouseChange() {
      console.log("选中的房屋ID：", this.formData.selectedHouse);
    },

    getSelectedHouseDetails() {
      return this.formData.selectedHouse.map((roomId) => {
        const room = this.roomMap[roomId] || {};
        const { rentAmount = 0, heatingAmount = 0 } =
          this.roomAmountMap[roomId] || {};
        return {
          roomName: room.roomName || "未知房屋",
          builtUpArea: room.builtUpArea || 0,
          roomSubTypeName: room.roomSubTypeName || "未知类型",
          stateName: room.stateName || "未知状态",
          rentAmount: rentAmount,
          heatingAmount: heatingAmount,
        };
      });
    },

    addPayment() {
      this.formData.payments.push({
        paymentDate: "",
        startDate: "",
        endDate: "",
        rentalDays: "",
        rentAmount: "",
        rentOnlyAmount: 0,
        heatingOnlyAmount: 0,
      });
      this.$nextTick(() => {
        this.calcPaymentAmounts();
        // 手工模式下新增后自动重算总计
        if (this.isManualMode) {
          this.reCalcTotalFromPayments();
        }
      });
    },

    removePayment(index) {
      if (this.formData.payments.length <= 1) {
        this.$message.warning("最少需保留1组付款信息");
        return;
      }
      this.formData.payments.splice(index, 1);
      this.calcPaymentAmounts();
      // 手工模式下删除后自动重算总计
      if (this.isManualMode) {
        this.reCalcTotalFromPayments();
      }
    },

    // calcPaymentAmounts() {
    //   // 手工模式跳过自动分摊
    //   if (this.isManualMode) return;
    //
    //   const needHeating = ["houseRentHeating"].includes(this.formData.templateType);
    //   // 统一使用高精度日租金计算
    //   const dayRentRate = this.rentRatePrecise || 0;
    //   const dayHeatRate = needHeating ? this.heatingFeePrecise   || 0 : 0;
    //   const totalDays = this.calcRentStandardDays();
    //   const payList = this.formData.payments;
    //
    //   if (totalDays <= 0 || payList.length === 0) return;
    //
    //   let sumRent = 0;
    //   let sumHeat = 0;
    //
    //   payList.forEach((payment) => {
    //     const days = Number(payment.rentalDays) || 0;
    //     payment.rentOnlyAmount = Number((dayRentRate * days).toFixed(2));
    //     payment.heatingOnlyAmount = Number((dayHeatRate * days).toFixed(2));
    //     payment.rentAmount = Number(
    //         (payment.rentOnlyAmount + payment.heatingOnlyAmount).toFixed(2),
    //     );
    //     sumRent += payment.rentOnlyAmount;
    //     sumHeat += payment.heatingOnlyAmount;
    //   });
    //
    //   // 尾差找平
    //   const totalRentAll = Number((dayRentRate * totalDays).toFixed(2));
    //   const totalHeatAll = Number((dayHeatRate * totalDays).toFixed(2));
    //   const diffRent = Number((totalRentAll - sumRent).toFixed(2));
    //   const diffHeat = Number((totalHeatAll - sumHeat).toFixed(2));
    //   const lastPay = payList[payList.length - 1];
    //
    //   lastPay.rentOnlyAmount = Number(
    //       (lastPay.rentOnlyAmount + diffRent).toFixed(2),
    //   );
    //   lastPay.heatingOnlyAmount = Number(
    //       (lastPay.heatingOnlyAmount + diffHeat).toFixed(2),
    //   );
    //   lastPay.rentAmount = Number(
    //       (lastPay.rentOnlyAmount + lastPay.heatingOnlyAmount).toFixed(2),
    //   );
    //   this.reCalcTotalFromPayments();
    // },

    calcPaymentAmounts() {
      // 手工模式跳过自动分摊
      if (this.isManualMode) return;

      const needHeating = ["houseRentHeating"].includes(this.formData.templateType);
      // 统一使用高精度日租金计算
      const dayRentRate = this.rentRatePrecise || 0;
      const dayHeatRate = needHeating ? this.heatingFeePrecise   || 0 : 0;
      const totalDays = this.calcRentStandardDays();
      const payList = this.formData.payments;

      if (totalDays <= 0 || payList.length === 0) return;

      let sumRent = 0;
      let sumHeat = 0;

      // 1、先不做toFixed，高精度计算原始值，存入临时字段，不截断
      payList.forEach((payment) => {
        const days = Number(payment.rentalDays) || 0;
        payment.rentOnlyAmount = dayRentRate * days;
        payment.heatingOnlyAmount = dayHeatRate * days;
        payment.rentAmount = payment.rentOnlyAmount + payment.heatingOnlyAmount;

        sumRent += payment.rentOnlyAmount;
        sumHeat += payment.heatingOnlyAmount;
      });

      // 总理论金额
      const totalRentAll = dayRentRate * totalDays;
      const totalHeatAll = dayHeatRate * totalDays;

      // 计算整体尾差
      const diffRent = totalRentAll - sumRent;
      const diffHeat = totalHeatAll - sumHeat;

      // 尾差全部给到最后一笔
      const lastPay = payList[payList.length - 1];
      lastPay.rentOnlyAmount += diffRent;
      lastPay.heatingOnlyAmount += diffHeat;
      lastPay.rentAmount = lastPay.rentOnlyAmount + lastPay.heatingOnlyAmount;

      // 全部算完之后，统一保留两位小数，此时相同天数，计算结果完全一致
      payList.forEach((payment) => {
        payment.rentOnlyAmount = Number(payment.rentOnlyAmount.toFixed(2));
        payment.heatingOnlyAmount = Number(payment.heatingOnlyAmount.toFixed(2));
        payment.rentAmount = Number((payment.rentOnlyAmount + payment.heatingOnlyAmount).toFixed(2));
      });

      this.reCalcTotalFromPayments();
    },

    goToNextStep() {
      if (this.isNavigating) return;
      this.isNavigating = true;
      this.validateAndNext();
    },

    validateAndNext() {
      const validateFields = this.getCurStepValidateFields();
      if (!Array.isArray(validateFields) || validateFields.length === 0) {
        this.activeStep++;
        this.isNavigating = false;
        return;
      }
      if (!this.$refs.contractForm) {
        this.isNavigating = false;
        return;
      }

      let completedCount = 0;
      const allErrors = {};

      const checkComplete = () => {
        completedCount++;
        if (completedCount === validateFields.length) {
          this.isNavigating = false;
          const hasError = Object.values(allErrors).some((err) => err);
          if (!hasError) {
            this.activeStep++;
          } else {
            this.$message.warning("请完善当前步骤的必填项并修正错误格式");
          }
        }
      };

      validateFields.forEach((field) => {
        this.$refs.contractForm.validateField(field, (errorMessage) => {
          allErrors[field] = errorMessage;
          checkComplete();
        });
      });
    },

    goToPrevStep() {
      this.activeStep--;
    },

    getCurStepValidateFields() {
      switch (this.activeStep) {
        case 0:
          return ["templateType"];
        case 1:
          return ["contractManager", "selectedHouse"];
        case 2:
          return [
            "partyBName",
            "partyBAddress",
            "partyBContact",
            "partyBPhone",
            "partyBEmail",
            "partyBLegal",
          ];
        case 3: {
          const baseFields = [
            "houseAddress",
            "signDate",
            "houseArea",
            "buildingUsage",
          ];
          if (["houseRentHeating"].includes(this.formData.templateType)) {
            baseFields.push("heatingFee");
          }
          if (this.formData.templateType === "houseRent") {
            baseFields.push(
              "decorationAmount",
              "investmentAmount",
              "depositAmount",
            );
          }
          return baseFields;
        }
        case 4: {
          const baseFields = [
            "marginDeposit",
            "incubationPeriod",
            "incubationPeriodTimeRange",
            "houseDecorationPeriodRange",
            "leaseTermRange",
            "rentStart",
            "rentEnd",
            "paymentMethod",
            "total",
            "rent",
          ];
          // 非手工模式才校验租金标准
          if (!this.isManualMode) {
            baseFields.push("rentRate");
          }
          const paymentFields = this.formData.payments
            .map((_, index) => [
              `payments[${index}].paymentDate`,
              `payments[${index}].startDate`,
              `payments[${index}].endDate`,
              `payments[${index}].rentalDays`,
              `payments[${index}].rentAmount`,
            ])
            .flat();
          return [...baseFields, ...paymentFields];
        }
        default:
          return [];
      }
    },

    // 手工模式：根据起止日期自动计算计租天数
    calcSinglePaymentDays(index) {
      if (!this.isManualMode) return;
      const payment = this.formData.payments[index];
      if (!payment.startDate || !payment.endDate) return;

      const start = new Date(payment.startDate);
      const end = new Date(payment.endDate);
      if (end < start) return;

      // 包含首尾两天，和原有总天数计算逻辑保持一致
      const diffTime = end.getTime() - start.getTime();
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      this.$set(this.formData.payments, index, {
        ...payment,
        rentalDays: days,
      });
    },

    handleDialogClose(done) {
      this.$confirm("确定要取消吗？已填写的内容将不会保存。")
        .then(() => done())
        .catch(() => {});
    },

    handleIncubationPeriodChange() {
      if (this.isManualMode) return;
      if (this.formData.paymentMethod && this.formData.rentStart) {
        this.generatePayments();
      }
    },

    handleRentStartDateChange() {
      this.isEditMode = false;
      if (this.isManualMode) return;
      if (this.formData.paymentMethod && this.formData.incubationPeriod) {
        this.generatePayments();
      }
    },

    handleRentEndDateChange() {
      this.isEditMode = false;
      if (this.isManualMode) return;
      if (this.formData.paymentMethod && this.formData.rentStart) {
        this.generatePayments();
      }
    },

    // 格式化单套租金：保留两位小数
    formatRentAmount(row) {
      const val = Number(row.rentAmount || 0);
      return val.toFixed(2);
    },

    formatHeatingAmount(row) {
      const val = Number(row.heatingAmount || 0);
      return val.toFixed(2);
    },

    generatePayments() {
      if (this.isEditMode || this.isManualMode) {
        return;
      }
      if (
        !this.formData.paymentMethod ||
        !this.formData.rentStart ||
        !this.formData.rentEnd
      ) {
        this.$message.warning("请先选择租金标准开始日期和截止日期");
        return;
      }
      const startDate = new Date(this.formData.rentStart);
      const endDate = new Date(this.formData.rentEnd);
      if (endDate <= startDate) {
        this.$message.error("租金标准截止日期必须晚于开始日期");
        return;
      }
      let intervalMonths;
      switch (this.formData.paymentMethod) {
        case "monthly":
          intervalMonths = 1;
          break;
        case "quarterly":
          intervalMonths = 3;
          break;
        case "half-yearly":
          intervalMonths = 6;
          break;
        case "yearly":
          intervalMonths = 12;
          break;
        default:
          return;
      }
      this.formData.payments = [];
      let currentStart = new Date(startDate);
      let currentEnd = new Date(currentStart);

      while (currentStart <= endDate) {
        currentEnd = new Date(currentStart);
        currentEnd.setMonth(currentEnd.getMonth() + intervalMonths);
        if (currentEnd > endDate) {
          currentEnd = new Date(endDate);
        } else {
          currentEnd.setDate(currentEnd.getDate() - 1);
        }
        const days =
          Math.ceil(
            (currentEnd.getTime() - currentStart.getTime()) /
              (1000 * 60 * 60 * 24),
          ) + 1;
        this.formData.payments.push({
          paymentDate: this.formatDateForPicker(currentStart),
          startDate: this.formatDateForPicker(currentStart),
          endDate: this.formatDateForPicker(currentEnd),
          rentalDays: days,
          rentAmount: 0,
          rentOnlyAmount: 0,
          heatingOnlyAmount: 0,
        });
        currentStart = new Date(currentEnd);
        currentStart.setDate(currentStart.getDate() + 1);
      }
      this.$nextTick(() => {
        this.calcAllRentAmount();
      });
    },

    reCalcTotalFromPayments() {
      const sum = this.formData.payments.reduce((acc, item) => {
        return acc + (Number(item.rentAmount) || 0);
      }, 0);
      const totalSum = Number(sum.toFixed(2));
      this.formData.total = totalSum;
      this.formData.rent = this.numberToChinese(totalSum);
    },

    calcRentStandardDays() {
      if (!this.formData.rentStart || !this.formData.rentEnd) return 0;
      const start = new Date(this.formData.rentStart);
      const end = new Date(this.formData.rentEnd);
      const diffMs = end.getTime() - start.getTime();
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1;
    },

    calcTotalLeaseDays() {
      if (
        !this.formData.leaseTermRange ||
        this.formData.leaseTermRange.length !== 2
      )
        return 0;
      const start = new Date(this.formData.leaseTermRange[0]);
      const end = new Date(this.formData.leaseTermRange[1]);
      const diffMs = end.getTime() - start.getTime();
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1;
    },

    calcAllRentAmount() {
      // 手工模式跳过自动计算
      if (this.isManualMode) return;

      const needHeating = ["houseRentHeating"].includes(this.formData.templateType);
      // 内部计算统一使用高精度值
      const dayRentRate = this.rentRatePrecise || 0;
      const dayHeatRate = needHeating ? this.heatingFeePrecise   || 0 : 0;
      const rentTotalDays = this.calcRentStandardDays();

      if (rentTotalDays <= 0) {
        this.formData.total = 0.0;
        this.formData.rent = "零元整";
        this.calcPaymentAmounts();
        return;
      }
      const totalAll = (dayRentRate + dayHeatRate) * rentTotalDays;
      const totalCalc = Number(totalAll.toFixed(2));

      this.formData.total = totalCalc;
      this.formData.rent = this.numberToChinese(totalCalc);
      this.$nextTick(() => {
        this.calcPaymentAmounts();
      });
    },

    async calcTotalRoomAmount(roomIds) {
      this.loadingRoomAmounts = true;
      try {
        const res = await queryRoomAmounts({ roomIds });
        const amountList = res.data || [];
        this.roomAmountMap = amountList.reduce((map, item) => {
          const roomId = item.roomId;
          if (!map[roomId]) {
            map[roomId] = { rentAmount: 0, heatingAmount: 0 };
          }
          // 单房间单日费用：面积 × 每平米日单价
          const dailyAmount = Number(item.rommArea) * Number(item.pricePerSqm);
          if (item.feeType === "rent") {
            map[roomId].rentAmount = dailyAmount;
          }
          if (item.feeType === "heating") {
            map[roomId].heatingAmount = dailyAmount;
          }
          return map;
        }, {});

        let totalRent = 0;
        let totalHeating = 0;

        roomIds.forEach((roomId) => {
          const { rentAmount = 0, heatingAmount = 0 } =
          this.roomAmountMap[roomId] || {};
          totalRent += rentAmount;
          totalHeating += heatingAmount;
        });

        this.totalRoomAmount = totalRent;

        // 采暖费：表单显示2位，内部计算用4位高精度
        if (
            ["houseRent", "houseRentNotHeating", "houseRentHeating"].includes(
                this.formData.templateType,
            )
        ) {
          this.heatingFeePrecise   = Number(totalHeating.toFixed(4));
          this.formData.heatingFee = Number(totalHeating.toFixed(2));
        }

        // 日租金：表单显示2位（满足校验），内部计算用4位高精度
        this.rentRatePrecise = Number(totalRent.toFixed(4));
        this.formData.rentRate = Number(totalRent.toFixed(2));
        this.formData.rentRateCount = Number((totalRent + totalHeating).toFixed(2));

        this.houseAmountUpdated = !this.houseAmountUpdated;
        await this.$nextTick();
        if (this.$refs.houseTable) this.$refs.houseTable.doLayout();
        this.calcAllRentAmount();
      } catch (error) {
        console.error("查询房屋金额失败：", error);
        this.$message.error("获取房屋金额失败，请手动填写租金标准");
        this.totalRoomAmount = 0;
        this.roomAmountMap = {};
        this.formData.rent = "";
        this.formData.total = 0;
        this.formData.heatingFee = 0;
        this.formData.rentRate = 0;
        this.formData.rentRateCount = 0;
        this.rentRatePrecise = 0;
        this.heatingFeePrecise   = 0;
      } finally {
        this.loadingRoomAmounts = false;
      }
    },

    formatDateForPicker(date) {
      if (!date) return null;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    updateIncubationPeriodText(dates) {
      if (dates && dates.length === 2) {
        this.formData.incubationPeriodText = `${dates[0]}至${dates[1]}`;
      } else {
        this.formData.incubationPeriodText = "";
      }
    },

    updateHouseDecorationPeriodText(dates) {
      if (dates && dates.length === 2) {
        this.formData.houseDecorationPeriod = `自${dates[0]}起至${dates[1]}`;
      } else {
        this.formData.houseDecorationPeriod = "";
      }
    },

    updateLeaseTermText(dates) {
      if (dates && dates.length === 2) {
        const start = new Date(dates[0]);
        const end = new Date(dates[1]);
        const timeDiff = end.getTime() - start.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
        this.formData.leaseTerm = `自${dates[0]}起至${dates[1]}止，共计${daysDiff}天`;
      } else {
        this.formData.leaseTerm = "";
      }
    },

    handleRentChange() {
      if (!this.formData.rent) return;
      const num = this.chineseToNumber(this.formData.rent);
      this.formData.total = Number(num.toFixed(2));
      if (!this.isManualMode) {
        this.calcPaymentAmounts();
      }
    },

    async submitContractForm() {
      if (!this.$refs.contractForm) return;
      if (this.isSaving) return;
      this.$refs.contractForm.validate(async (valid) => {
        if (valid) {
          try {
            this.isSaving = true;
            const submitData = {
              communityId: this.communityId,
              templateCode: this.formData.templateType,
              contractManager: this.formData.contractManager,
              selectedHouse: this.formData.selectedHouse,
              heatingFee: this.formData.heatingFee,
              partyBName: this.formData.partyBName,
              partyBAddress: this.formData.partyBAddress,
              partyBContact: this.formData.partyBContact,
              partyBPhone: this.formData.partyBPhone,
              partyBEmail: this.formData.partyBEmail,
              partyBmAilingAddress: this.formData.partyBmAilingAddress,
              partyBLegal: this.formData.partyBLegal,
              rent: this.formData.rent,
              houseAddress: this.formData.houseAddress,
              signDate: this.formatDateToChinese(this.formData.signDate),
              houseArea: this.formData.houseArea,
              buildingUsage: this.formData.buildingUsage,
              decorationAmount: this.formData.decorationAmount,
              investmentAmount: this.formData.investmentAmount,
              depositAmount: this.formData.depositAmount,
              marginDeposit: this.formData.marginDeposit,
              incubationPeriod: this.formData.incubationPeriod,
              incubationPeriodTime: this.formData.incubationPeriodText,
              houseDecorationPeriod: this.formData.houseDecorationPeriod,
              leaseTerm: this.formData.leaseTerm,
              rentStart: this.formatDateToChinese(this.formData.rentStart),
              rentEnd: this.formatDateToChinese(this.formData.rentEnd),
              total: this.formData.total,
              rentRate: this.formData.rentRateCount,
              policySource: this.formData.policySource,
              policyInfo: this.formData.policyInfo,
              paymentMethod: this.formData.paymentMethod,
              payments: this.formData.payments.map((payment, index) => {
                const rentAmountVal = Number(payment.rentAmount || 0);
                return {
                  paymentNo: index + 1,
                  paymentId:
                    payment.paymentId !== undefined ? payment.paymentId : null,
                  paymentDate: this.formatDateToChinese(payment.paymentDate),
                  startDate: this.formatDateToChinese(payment.startDate),
                  endDate: this.formatDateToChinese(payment.endDate),
                  rentalDays: payment.rentalDays,
                  rentAmount: rentAmountVal,
                  // 手工模式：纯租金金额 = 付款总金额，采暖费置为0
                  rentOnlyAmount: this.isManualMode
                    ? rentAmountVal
                    : payment.rentOnlyAmount,
                  heatingOnlyAmount: this.isManualMode
                    ? 0
                    : payment.heatingOnlyAmount,
                };
              }),
            };
            if (this.currentContractId) {
              submitData.contractId = this.currentContractId;
            }
            const response = await queryWordByContract(submitData);
            if (response.code !== 0)
              throw new Error(`${response.msg || "服务器异常"}`);
            if (!response.data) {
              this.$message.error("未获取到Word文件数据");
            } else {
              const fileName = `${this.getTemplateName()}_${new Date().getTime()}.docx`;
              this.downloadFile(response.data, fileName);
              this.$message.success("合同Word文档生成成功，已开始下载！");
            }

            this.dialogVisible = false;
            this.$emit("success");
          } catch (error) {
            console.error("合同生成失败：", error);
            this.$message.error(
              `操作失败：${error.message || "网络异常，请重试"}`,
            );
          } finally {
            this.isSaving = false;
          }
        } else {
          this.$message.warning("请完善所有必填项并修正错误格式");
        }
      });
    },

    downloadFile(base64Content, fileName) {
      try {
        let decodedBase64 = base64Content
          .trim()
          .replace(/-/g, "+")
          .replace(/_/g, "/");
        while (decodedBase64.length % 4) decodedBase64 += "=";
        const byteCharacters = atob(decodedBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++)
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document;charset=utf-8",
        });
        if (window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(blob, fileName);
        } else {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          a.style.display = "none";
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 500);
        }
      } catch (error) {
        console.error("文件下载失败：", error);
        this.$message.error(`下载失败：${error.message || "文件格式错误"}`);
      }
    },

    getTemplateName() {
      const templateMap = {
        houseRent: "房屋租赁协议（装修抵租金不含采暖费）",
        houseRentNew: "光谷房屋租赁协议最新版",
        houseRentHeating: "房屋租赁协议（含采暖费）",
        houseRentNotHeating: "房屋租赁协议（不含采暖费）",
      };
      return templateMap[this.formData.templateType] || "房屋租赁协议";
    },

    formatDateToChinese(dateStr) {
      if (!dateStr) return "";
      const [year, month, day] = dateStr.split("-");
      return `${year}年${month}月${day}日`;
    },
  },
  beforeDestroy() {
    if (this.paymentDebounceTimer) clearTimeout(this.paymentDebounceTimer);
  },
  async created() {
    this.communityId = await getCommunityId();
    this.loadStaffList();
  },
};
</script>

<style scoped>
.contract-form {
  margin-top: 15px;
}

.dialog-footer {
  text-align: right;
  margin-top: 20px;
}

.el-form-item {
  margin-bottom: 16px;
}

.required-item .el-form-item__label::before {
  content: "*";
  color: #ff4d4f;
  margin-right: 4px;
}

.template-desc {
  margin-top: 20px;
  background: #f5fafe;
  border: 1px solid #e8f4f8;
}

.template-tips {
  margin-top: 10px;
  padding: 8px 12px;
  background: #e8f4f8;
  border-radius: 4px;
  color: #4299e1;
  font-size: 13px;
}

.template-tips .el-icon-info {
  margin-right: 6px;
}

.el-divider {
  margin: 20px 0;
}

.el-divider__text {
  color: #4299e1;
  font-weight: 500;
}

.payment-group {
  margin-bottom: 25px;
  padding: 15px;
  background: #fafafa;
  border-radius: 8px;
}

.payment-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
</style>
